// src/components/ModelDetail.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import VisorRA from './VisorRA';
import { Dialog, CircularProgress, IconButton, Tooltip } from '@mui/material';
import QRCode from 'react-qr-code';
import { Share as ShareIcon, Close as CloseIcon } from '@mui/icons-material';
import './ModelDetail.css';

const ModelDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);

  const [anchors, setAnchors] = useState([]);
  const [anchorsLoading, setAnchorsLoading] = useState(false);

  const [openARViewer, setOpenARViewer] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);

  // URL para el QR (siempre apunta a /model/:id?ar=1)
  const qrUrl = useMemo(() => {
    return model?.id ? `${window.location.origin}/model/${model.id}?ar=1` : '';
  }, [model?.id]);

  // Cargar documento del modelo
  useEffect(() => {
    let alive = true;
    const fetchModel = async () => {
      try {
        setLoading(true);
        const ref = doc(db, 'models', id);
        const snap = await getDoc(ref);
        if (!alive) return;

        if (snap.exists()) {
          setModel({ id: snap.id, ...snap.data() });
        } else {
          setModel(null);
        }
      } catch (error) {
        console.error('Error getting model:', error);
        if (alive) setModel(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (id) fetchModel();
    return () => { alive = false; };
  }, [id]);

  // Cargar anclas del modelo
  useEffect(() => {
    let alive = true;
    const fetchAnchors = async (modelId) => {
      try {
        setAnchorsLoading(true);
        const qRef = query(
          collection(db, 'models', modelId, 'anchors'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(qRef);
        if (!alive) return;
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        setAnchors(list);
      } catch (e) {
        console.error('Error getting anchors:', e);
        if (alive) setAnchors([]);
      } finally {
        if (alive) setAnchorsLoading(false);
      }
    };

    if (model?.id) fetchAnchors(model.id);
    return () => { alive = false; };
  }, [model?.id]);

  // Autoabrir visor AR cuando viene ?ar=1 y hay URL del modelo
  useEffect(() => {
    if (!model?.id) return;
    const shouldOpen = searchParams.get('ar') === '1';
    if (shouldOpen && model.modelUrl) {
      setOpenARViewer(true);
    }
  }, [model?.id, model?.modelUrl, searchParams]);

  const handleShareAR = () => setOpenQRDialog(true);

  if (loading) {
    return (
      <div className="model-detail-loading">
        <CircularProgress size={56} sx={{ color: '#6de8ff' }} />
      </div>
    );
  }

  if (!model) {
    return (
      <div className="model-detail-not-found">
        <h2>Modelo no encontrado</h2>
        <p>El modelo que buscas no existe o ha sido eliminado.</p>
      </div>
    );
  }

  return (
    <div className="model-detail-container">
      <div className="model-header">
        <h1>{model.name}</h1>
        <div className="header-actions">
          <Tooltip title="Compartir en RA">
            <IconButton
              onClick={handleShareAR}
              className="share-button"
              aria-label="Compartir en RA"
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className="model-content">
        <div className="model-image-container">
          {model.thumbnailUrl ? (
            <img
              src={model.thumbnailUrl}
              alt={model.name}
              className="model-image"
            />
          ) : (
            <div className="image-placeholder">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        <div className="model-info">
          <div className="model-meta">
            <div className="meta-item">
              <span className="meta-label">Categoría:</span>
              <span className="meta-value">
                {model.category || 'No especificado'}
              </span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Época:</span>
              <span className="meta-value">{model.era || 'No especificada'}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Ubicación:</span>
              <span className="meta-value">
                {model.location || 'No especificada'}
              </span>
            </div>
          </div>

          <div className="model-detail-description">
            <h3>Descripción</h3>
            <p>{model.description || 'No hay descripción disponible.'}</p>
          </div>

          <div className="model-actions">
            <button
              onClick={() => setOpenARViewer(true)}
              className="ar-button large"
              disabled={!model.modelUrl}
              title={!model.modelUrl ? 'Este modelo no tiene URL 3D' : 'Abrir visor AR'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                />
              </svg>
              Ver en Realidad Aumentada
            </button>

            <button onClick={handleShareAR} className="qr-button large">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                />
              </svg>
              Obtener código QR
            </button>
          </div>
        </div>
      </div>

      {/* Diálogo para el visor AR */}
      <Dialog
        open={openARViewer}
        onClose={() => setOpenARViewer(false)}
        fullScreen
        maxWidth="xl"
        PaperProps={{
          sx: {
            backgroundColor: 'black',
            overflow: 'hidden',
            height: '100dvh'
          }
        }}
      >
        <VisorRA
          modelId={model.id}
          initialModelUrl={model.modelUrl}
          anchors={anchors}
          anchorsLoading={anchorsLoading}
          onClose={() => setOpenARViewer(false)}
          isMobile={typeof window !== 'undefined' ? window.innerWidth < 768 : false}
        />
      </Dialog>

      {/* Diálogo para el código QR */}
      <Dialog
        open={openQRDialog}
        onClose={() => setOpenQRDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="qr-dialog-container">
          <div className="qr-dialog-header">
            <h2>Compartir en Realidad Aumentada</h2>
            <IconButton onClick={() => setOpenQRDialog(false)} className="close-button" aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </div>
          <div className="qr-dialog-content">
            <p>Escaneá este código para abrir el modelo en RA (con anclas):</p>
            <div className="qr-code-container">
              {qrUrl && (
                <QRCode value={qrUrl} size={256} level="H" bgColor="#ffffff" fgColor="#000000" />
              )}
            </div>
            <p className="qr-url">{qrUrl}</p>
            <button
              onClick={() => qrUrl && navigator.clipboard.writeText(qrUrl)}
              className="copy-button"
              disabled={!qrUrl}
            >
              Copiar enlace
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ModelDetail;
